import { useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import { GridLayout } from 'shared/ui/grid-layout';
import { BankAccountDto, BankAccountType, BA_TYPES, CreateBankAccountDto, UpdateBankAccountDto } from '@sr/dto';
import { Button, IconButton, TableRow, TableCell, Typography, Box, Stack, FormControlLabel, MenuItem, Switch } from '@mui/material';
import { FormData, formTools } from './bank-account.form-tools';
import { NotificationsConfig } from 'shared/ui/snack';
import { useCreateOrEditDialog } from 'hooks/create-or-edit-dialog.hook';
import { useBankAccounts } from 'hooks/bank-accounts.hook';
import { FormDialog } from 'shared/ui/form-dialog';
import { Field } from 'formik';
import { TextField } from 'formik-mui';
import { LegalEntityPicker } from 'entities/legal-entities/legal-entity-picker';
import { useOurCompany } from 'hooks/portal-settings.hook';
import { BankDetailsPicker } from 'shared/ui/formik-fields/bank-details-picker';
import { useBankDetailsDialog } from '../../../shared/providers/legal-entities.provider';
import { ErrorBanner } from '../../../shared/ui/error-banner';

const columns = ['Активен', 'Название', 'Юр. лицо / реквизиты', 'Тип', ''];

const notificationsConfig: NotificationsConfig = {
  itemTitleForm1: 'расчетный счет',
  itemTitleForm2: 'расчетного счета',
  gender: 'male',
};

const BankAccountsList = () => {
  const { collection, addItem, updateItem } = useBankAccounts();
  const [legalEntityId, setLegalEntityId] = useState(0);
  const { dialogProps, openToAdd, openToEdit, itemToEdit } = useCreateOrEditDialog<BankAccountDto, CreateBankAccountDto, UpdateBankAccountDto, FormData>({
    addHandler: async (dto) => {
      await addItem(dto);
    },
    updateHandler: async (dto) => {
      await updateItem(dto.id, dto);
    },
    formTools: formTools,
    notificationsConfig,
  });
  const ourCompanyId = useOurCompany();
  const ourBankDetailsDialog = useBankDetailsDialog(legalEntityId);
  if (collection.error) {
    return <ErrorBanner errorMessage={collection.error} />;
  }

  // номера расчётных счетов, встречающиеся больше одного раза среди активных счетов — дубли в справочнике
  const duplicateAccountNumbers = new Set(
    Object.entries(
      collection.items.reduce<Record<string, number>>((acc, a) => {
        if (!a.isActive) return acc;
        const num = a.bankDetails?.accountNumber;
        if (num) acc[num] = (acc[num] ?? 0) + 1;
        return acc;
      }, {}),
    )
      .filter(([, count]) => count > 1)
      .map(([num]) => num),
  );
  return (
    <Box>
      <Button size="small" variant="contained" sx={{ mb: 2 }} onClick={openToAdd}>
        Добавить
      </Button>

      <GridLayout<BankAccountDto>
        columns={columns}
        items={collection.items}
        isLoading={collection.isLoading}
        error={collection.error}
        itemRender={(account) => (
          <Row
            key={account.id}
            account={account}
            isDuplicate={!!account.bankDetails?.accountNumber && duplicateAccountNumbers.has(account.bankDetails.accountNumber)}
            onEditClick={() => openToEdit(account)}
            // onDeleteClick={() => handleDelete(account)}
          />
        )}
        noItemsText="Список счетов пуст"
      />

      <FormDialog<FormData>
        {...dialogProps}
        renderTitle={!itemToEdit ? 'Новый расчетный счет' : 'Расчетный счет'}
        renderForm={(props) => {
          const legalEntityIdFilter = props.values.legalEntityId;
          setLegalEntityId(legalEntityIdFilter as number);
          return (
            <Stack direction="column">
              <Box>{itemToEdit && <Field name="isActive" control={<Switch />} type="checkbox" as={FormControlLabel} label="Активен" />}</Box>
              <Box>
                <Field
                  name="allowOutgoingInvoices"
                  control={<Switch />}
                  type="checkbox"
                  as={FormControlLabel}
                  label="Разрешён для ИСХ счетов"
                />
              </Box>

              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Field required name="title" component={TextField} label="Название" fullWidth />
                <Field name="type" component={TextField} select fullWidth label="Тип" sx={{ width: 250 }}>
                  {/* <MenuItem value=''>Выберите тип...</MenuItem> */}
                  {Object.keys(BA_TYPES).map((key) => (
                    <MenuItem key={key} value={key}>
                      {BA_TYPES[Number(key) as BankAccountType]}
                    </MenuItem>
                  ))}
                </Field>
              </Stack>
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Box sx={{ flexBasis: 250, flexGrow: 1 }}>
                  <Field label="Наше юр. лицо" component={LegalEntityPicker} companyIdFilter={ourCompanyId} fullWidth name="legalEntityId" />
                </Box>
                <Box sx={{ flexBasis: 250, flexGrow: 1 }}>
                  <Field
                    label="Наши реквизиты"
                    name="bankDetailsId"
                    fullWidth
                    component={BankDetailsPicker}
                    legalEntityId={legalEntityIdFilter}
                    collection={ourBankDetailsDialog.collection}
                    disabled={!legalEntityIdFilter}
                  />
                </Box>
              </Stack>
            </Stack>
          );
        }}
      />
    </Box>
  );
};

type Props = {
  account: BankAccountDto;
  isDuplicate?: boolean;
  onEditClick: () => void;
};

const Row = ({ account, isDuplicate, onEditClick }: Props) => {
  const mutedColor = account.isActive ? 'text.secondary' : 'grey.500';
  const le = account.legalEntity;
  const bd = account.bankDetails;

  // подписи под названием показываем только для активных счетов
  const notes: { text: string; color: string }[] = [];
  if (account.isActive) {
    if (isDuplicate) notes.push({ text: 'Дубль: этот расчётный счёт заведён несколько раз', color: 'warning.dark' });
    if (account.allowOutgoingInvoices === false) notes.push({ text: 'Запрещён для исходящих счетов', color: 'error.main' });
  }

  return (
    <TableRow>
      <TableCell>{account.isActive ? <CheckCircleIcon color="primary" /> : <BlockIcon sx={{ color: 'grey.500' }} />}</TableCell>
      <TableCell>
        <Typography variant="body2" color={account.isActive ? 'inherit' : 'grey.500'}>
          {account.title}
        </Typography>
        {notes.map((note) => (
          <Typography key={note.text} variant="caption" color={note.color} sx={{ display: 'block' }}>
            {note.text}
          </Typography>
        ))}
      </TableCell>
      <TableCell>
        <Typography variant="caption" color={mutedColor} sx={{ display: 'block' }}>
          {le ? `${le.name} (ИНН ${le.inn})` : '— юр. лицо не указано —'}
        </Typography>
        <Typography variant="caption" color={mutedColor} sx={{ display: 'block' }}>
          {bd ? `${bd.bankName} (БИК ${bd.bik})` : '— реквизиты не указаны —'}
        </Typography>
        {bd && (
          <Typography variant="caption" color={mutedColor} sx={{ display: 'block' }}>
            р/с {bd.accountNumber}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color={account.isActive ? 'inherit' : 'grey.500'}>
          {BA_TYPES[account.type]}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <IconButton edge="end" onClick={onEditClick}>
          <EditIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export default BankAccountsList;
